// Admin authentication utilities
export const ADMIN_STORAGE_KEY = 'oha_admin_auth';
export const USERS_STORAGE_KEY = 'oha_users_data';
export const PAGES_STORAGE_KEY = 'oha_pages_data';
export const CONTACT_STORAGE_KEY = 'oha_contact_data';

// Default admin user
const DEFAULT_ADMIN = {
  id: 1,
  username: 'admin',
  email: 'admin@ohasystem.com',
  password: 'admin123', // In production, this would be hashed
  role: 'super_admin',
  name: 'System Administrator',
  createdAt: new Date().toISOString(),
  isActive: true
};

// Initialize default data
export const initializeDefaultData = () => {
  // Initialize users if not exists
  if (!localStorage.getItem(USERS_STORAGE_KEY)) {
    const users = [DEFAULT_ADMIN];
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
  }

  // Initialize contact details if not exists
  if (!localStorage.getItem(CONTACT_STORAGE_KEY)) {
    const contactData = {
      email: 'contact@ohasystem.com',
      phone: '+1 (555) 123-4567',
      address: 'San Francisco, CA',
      businessHours: 'Mon-Fri: 9AM-6PM PST',
      socialMedia: {
        linkedin: '#',
        twitter: '#',
        facebook: '#'
      }
    };
    localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contactData));
  }

  // Initialize page data if not exists
  if (!localStorage.getItem(PAGES_STORAGE_KEY)) {
    const pagesData = {
      home: {
        heroTitle: 'Transform Your Business with Agentic AI Solutions',
        heroSubtitle: 'Empowering small businesses and startups with cutting-edge AI technology, strategic consulting, and innovative solutions that drive unprecedented growth.',
        ctaText: 'Get Free Basic Consultation',
        lastUpdated: new Date().toISOString()
      },
      services: {
        title: 'Our Services',
        subtitle: 'Comprehensive solutions designed to transform your business through innovative technology and strategic consulting.',
        lastUpdated: new Date().toISOString()
      },
      about: {
        title: 'About OHA System',
        subtitle: 'We are passionate technologists and strategists dedicated to empowering small businesses and startups with the transformative power of AI and strategic consulting.',
        mission: 'To democratize access to cutting-edge AI technology and strategic consulting, enabling small businesses and startups to compete and thrive in the digital economy.',
        lastUpdated: new Date().toISOString()
      },
      caseStudies: {
        title: 'Case Studies',
        subtitle: 'Real success stories showcasing how our AI solutions and strategic consulting have transformed businesses across industries.',
        lastUpdated: new Date().toISOString()
      },
      blog: {
        title: 'OHA System Blog',
        subtitle: 'Insights, strategies, and best practices for leveraging AI and technology to transform your business operations and drive growth.',
        lastUpdated: new Date().toISOString()
      },
      contact: {
        title: 'Contact Us',
        subtitle: 'Ready to transform your business? Let\'s start the conversation about how our AI solutions can drive your success.',
        lastUpdated: new Date().toISOString()
      }
    };
    localStorage.setItem(PAGES_STORAGE_KEY, JSON.stringify(pagesData));
  }
};

// Authentication functions
export const login = (username, password) => {
  const usersData = JSON.parse(localStorage.getItem(USERS_STORAGE_KEY) || '[]');
  const user = usersData.find(u => u.username === username && u.password === password && u.isActive);
  
  if (user) {
    const authData = {
      user: { ...user, password: undefined }, // Don't store password in auth data
      token: generateToken(),
      loginTime: new Date().toISOString()
    };
    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(authData));
    return authData;
  }
  return null;
};

export const logout = () => {
  localStorage.removeItem(ADMIN_STORAGE_KEY);
};

export const getCurrentUser = () => {
  const authData = localStorage.getItem(ADMIN_STORAGE_KEY);
  if (authData) {
    try {
      return JSON.parse(authData);
    } catch (e) {
      return null;
    }
  }
  return null;
};

export const isAuthenticated = () => {
  return getCurrentUser() !== null;
};

export const hasPermission = (requiredRole = 'admin') => {
  const authData = getCurrentUser();
  if (!authData) return false;
  
  const userRole = authData.user.role;
  const roleHierarchy = ['admin', 'super_admin'];
  const requiredIndex = roleHierarchy.indexOf(requiredRole);
  const userIndex = roleHierarchy.indexOf(userRole);
  
  return userIndex >= requiredIndex;
};

// Generate simple token (in production, use proper JWT)
const generateToken = () => {
  return Math.random().toString(36).substr(2) + Date.now().toString(36);
};

// User management functions
export const getUsers = () => {
  return JSON.parse(localStorage.getItem(USERS_STORAGE_KEY) || '[]');
};

export const addUser = (userData) => {
  const users = getUsers();
  const newUser = {
    id: Date.now(),
    ...userData,
    createdAt: new Date().toISOString(),
    isActive: true
  };
  users.push(newUser);
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
  return newUser;
};

export const updateUser = (userId, userData) => {
  const users = getUsers();
  const index = users.findIndex(u => u.id === userId);
  if (index !== -1) {
    users[index] = { ...users[index], ...userData, updatedAt: new Date().toISOString() };
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    return users[index];
  }
  return null;
};

export const deleteUser = (userId) => {
  const users = getUsers();
  const filteredUsers = users.filter(u => u.id !== userId);
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(filteredUsers));
  return true;
};

// Page data functions
export const getPageData = (page) => {
  const pagesData = JSON.parse(localStorage.getItem(PAGES_STORAGE_KEY) || '{}');
  return pagesData[page] || {};
};

export const updatePageData = (page, data) => {
  const pagesData = JSON.parse(localStorage.getItem(PAGES_STORAGE_KEY) || '{}');
  pagesData[page] = { ...pagesData[page], ...data, lastUpdated: new Date().toISOString() };
  localStorage.setItem(PAGES_STORAGE_KEY, JSON.stringify(pagesData));
  return pagesData[page];
};

// Contact data functions
export const getContactData = () => {
  return JSON.parse(localStorage.getItem(CONTACT_STORAGE_KEY) || '{}');
};

export const updateContactData = (data) => {
  const contactData = { ...getContactData(), ...data, lastUpdated: new Date().toISOString() };
  localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contactData));
  return contactData;
};

// Initialize data on app start
initializeDefaultData();